/**
 * rb-jd.js — job-posting keyword match (instant, local, no AI).
 *
 * Extracts the salient terms of a pasted posting and checks which ones the
 * resume shows. Fixed 2026-10-09 (audit): sentence punctuation stuck to words
 * ("java." and "kubernetes." became separate keywords), and a plain substring
 * test counted "Java" as found inside "JavaScript". Matching is now by whole
 * word (Unicode letters/digits as the word characters), and the stop-words
 * follow the language of the POSTING, not of the page.
 *
 * Depends on: RbState. globalThis.RbJd
 */
(function () {
    'use strict';

    var STOP = {
        en: 'the a an and or to of in on for with at by from as is are be will your you we our their this that has have can must should would etc job role work team experience years year skills ability strong good knowledge plus including within across into about who what which when where how all any new more such other well also able required preferred responsibilities requirements',
        ru: 'и в во не на он но что а как из за от до по об при так это для уже или быть есть мы вы наш ваш роль работа команда опыт лет год года навыки знание знания умение требования обязанности условия будет плюсом наличие хорошее хорошо уверенный уверенное работы компании компания вас нас нам вам который которые также',
        uk: 'і в у не на він але що а як це по до вони так його всі ж би було бути від вже ми ви наш ваш роль робота команда та або є буде мають можна повинен треба для про між досвід років знання вимоги обовязки',
        de: 'der die das und oder zu von in im für mit den dem ein eine als ist sind wir sie ihre unsere arbeit team rolle erfahrung kenntnisse jahre sowie bei auf',
        es: 'el la los las y o de en para con un una que es son ser su sus nuestro trabajo equipo rol como del al experiencia años conocimientos',
        fr: 'le la les et ou de en pour avec un une que est sont etre son ses notre travail equipe role du des au expérience ans connaissances',
        pt: 'o a os as e ou de em para com um uma que e sao ser seu sua nosso trabalho equipe funcao do da no experiência anos conhecimento',
        id: 'dan atau di ke dari yang untuk dengan pada adalah akan kami anda kerja tim peran sebagai itu ini pengalaman tahun',
        ko: '및 그리고 또는 의 를 을 에 에서 으로 하는 합니다 있는 우리 귀하 업무 팀 역할 경험',
        ar: 'و او في من الى على عن مع ان هذا هذه التي الذي العمل الفريق الدور كما خبرة',
        tr: 've veya ile bir bu şu o için gibi ama ancak çünkü ki de da mi biz siz onlar bizim sizin var yok olan olarak iş rol ekip takım çalışma deneyim'
    };

    function langOf(text, fallback) {
        var s = String(text || '');
        // Ukrainian or Russian: the site's one detector (timbrica-core), not «is there an і» —
        // a Russian vacancy with an office in «Київ» took the Ukrainian stop words.
        if (/[а-яёіїєґ]/i.test(s)) {
            var cl = typeof window !== 'undefined' ? window.__cyrillicLang : null;
            return typeof cl === 'function' && cl(s) === 'uk' ? 'uk' : 'ru';
        }
        if (/[가-힣]/.test(s)) return 'ko';
        if (/[؀-ۿ]/.test(s)) return 'ar';
        return (fallback || 'en').slice(0, 2);
    }
    function stopSet(lang) {
        var set = {};
        (STOP.en + ' ' + (STOP[lang] || '')).split(/\s+/).forEach(function (w) { if (w) set[w] = 1; });
        return set;
    }
    /* "kubernetes." → "kubernetes", "-sql" → "sql"; keeps "c++", "c#", ".net", "node.js" */
    function tidy(w) {
        w = w.replace(/^[-.]+(?!net\b)/, '').replace(/[.,:;!?)\]»"'-]+$/, '');
        return w;
    }
    function tokens(text) {
        return String(text || '').toLowerCase().replace(/ё/g, 'е')
            .replace(/[^\p{L}\p{N}\s+#./-]/gu, ' ')
            .split(/[\s/]+/).map(tidy).filter(Boolean);
    }

    /* Light stemming for Russian and Ukrainian: «главный», «главным», «главного»
       are one requirement. Without it the same word counted as found and missing. */
    /* strip the case ending, then compare six letters: «проверок»/«проверки»,
       «бухгалтер»/«бухгалтером», «налоговый»/«налоговых» meet; a rare collision
       («производство»/«производственный») costs less than a missed match. */
    var RU_END = /(иями|ями|ами|ого|его|ому|ему|ыми|ими|ией|иях|ях|ах|ой|ей|ий|ый|ым|им|ых|их|ая|яя|ое|ее|ые|ие|ую|юю|ам|ям|ом|ем|ов|ев|ию|ья|ье|ьи|ы|и|а|я|о|е|у|ю|ь|і|ї)$/;
    function stem(w, lang) {
        if ((lang === 'ru' || lang === 'uk') && /[а-яіїєґ]/.test(w) && w.length > 3) {
            var s = w.replace(RU_END, '');
            if (s.length < 3) s = w;
            return s.slice(0, 6);
        }
        return w;
    }
    /* job-ad filler, matched by stem */
    var FILLER = { ru: 'компан органи ваканс требуе ищем предла обязан требов услови плюс преиму налич умени знани понима навык работ опыт лет год челове коллек офис график зарпла оформл', uk: 'компан органі ваканс вимог обовяз умов перева досвід робот рок людин' };

    function extract(text, locale, limit) {
        limit = limit || 24;
        var lang = langOf(text, locale);
        var stop = stopSet(lang);
        var filler = {};
        (FILLER[lang] || '').split(' ').forEach(function (f) { if (f) filler[f] = 1; });
        var toks = tokens(text).filter(function (w) {
            if (w.length <= 2 || stop[w] || /^\d+$/.test(w)) return false;
            var s = stem(w, lang);
            return !Object.keys(filler).some(function (f) { return s.indexOf(f) === 0; });
        });
        /* one representative per stem */
        var seen = {};
        toks = toks.map(function (w) { var s = stem(w, lang); if (!seen[s]) seen[s] = w; return seen[s]; });
        var freq = {};
        toks.forEach(function (w) { freq[w] = (freq[w] || 0) + 1; });
        for (var i = 0; i < toks.length - 1; i++) {
            var bg = toks[i] + ' ' + toks[i + 1];
            freq[bg] = (freq[bg] || 0) + 1.5;
        }
        return Object.keys(freq)
            .filter(function (k) { return k.indexOf(' ') < 0 ? freq[k] >= 1 : freq[k] >= 3; })
            .sort(function (a, b) { return freq[b] - freq[a]; })
            .slice(0, limit);
    }

    function reEsc(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
    /* whole-word presence; letters and digits are word characters in every script */
    function contains(hay, k) {
        try {
            return new RegExp('(^|[^\\p{L}\\p{N}])' + reEsc(k) + '($|[^\\p{L}\\p{N}+#])', 'u').test(hay);
        } catch (e) { return hay.indexOf(k) > -1; }
    }

    function analyze(model, jdText, locale) {
        var keywords = extract(jdText, locale);
        var lang = langOf(jdText, locale);
        var hay = globalThis.RbState.allText(model).toLowerCase().replace(/ё/g, 'е');
        var stems = {};
        tokens(hay).forEach(function (w) { stems[stem(w, lang)] = 1; });
        var has = function (k) {
            if (contains(hay, k)) return true;
            return k.split(' ').every(function (w) { return stems[stem(w, lang)]; });
        };
        var found = [], missing = [];
        keywords.forEach(function (k) { (has(k) ? found : missing).push(k); });
        return {
            keywords: keywords, found: found, missing: missing,
            matchPct: keywords.length ? Math.round((found.length / keywords.length) * 100) : 0
        };
    }

    globalThis.RbJd = { extract: extract, analyze: analyze, contains: contains, langOf: langOf, stem: stem };
})();
